// ResearchItem.jsx — version améliorée (large card/list item)
import React, { memo, useMemo } from "react";
import {
  Card,
  CardActionArea,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  useMediaQuery,
  styled,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { format, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import Iconify from "./Iconify";
import { handleFirebaseTimestamp } from "./api/api";

// ---------- Utils (Firestore Timestamp | Date | ISO) ----------
const toDateSafe = (d) => {
  if (!d) return null;

  let out = d;

  // Firestore Timestamp (objet ou FieldValue)
  if (typeof d?.toDate === "function") out = d.toDate();
  else if (d && typeof d === "object" && Number.isFinite(d?.seconds)) {
    out = new Date(d.seconds * 1000);
  }
  // Primitives usuelles
  else if (typeof d === "string" || typeof d === "number") {
    // ⚠️ Les strings type "24/02/2013" ne sont pas ISO → new Date() échoue.
    // Privilégie des ISO "2025-10-30T20:00:00Z" ou passe un Timestamp Firestore.
    out = new Date(d);
  }

  // Dernière barrière
  if (!(out instanceof Date) || !isValid(out)) return null;
  return out;
};

const safeFormat = (date, fmt) => (date && isValid(date) ? format(date, fmt, { locale: fr }) : "");

// 3) Range robuste
const formatRange = (start, end) => {
  const s = toDateSafe(start);
  const e = toDateSafe(end);

  if (!s && !e) return "";
  if (s && !e) return safeFormat(s, "dd MMM yyyy • HH:mm");
  if (!s && e) return safeFormat(e, "dd MMM yyyy • HH:mm");

  // s & e valides
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate();

  if (sameDay) {
    return `${safeFormat(s, "dd MMM yyyy")} • ${safeFormat(s, "HH:mm")}`;
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${safeFormat(s, "dd MMM • HH:mm")} — ${safeFormat(e, "dd MMM • HH:mm")}`;
  }
  return `${safeFormat(s, "dd MMM yyyy • HH:mm")} — ${safeFormat(e, "dd MMM yyyy • HH:mm")}`;
};

const formatPrice = (minPrice, currency = "CFA") => {
  if (minPrice === 0 || String(minPrice).toLowerCase() === "gratuit") return "Gratuit";
  if (minPrice == null || minPrice === "") return "Tarifs variables";
  const nf = new Intl.NumberFormat("fr-FR");
  const num = Number(minPrice);
  return Number.isFinite(num) ? `${nf.format(num)} ${currency}` : `${minPrice} ${currency}`;
};

// ---------- Styled en dehors du composant (évite recréation à chaque render) ----------
const EventBox = styled(Card)(({ theme }) => ({
  borderRadius: 10,
  boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
  transition: "box-shadow .25s ease, transform .2s ease",
  "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.12)", transform: "translateY(-1px)" },
  backgroundColor: "#fff",
  margin: theme.spacing(0.75, 2),
  [theme.breakpoints.down("md")]: { margin: theme.spacing(1, 0) },
}));

const Img = styled("img")(({ theme }) => ({
  width: 220,
  height: 160,
  borderRadius: 8,
  objectFit: "cover",
  background: "#f2f2f2",
  [theme.breakpoints.down("md")]: { width: 120, height: 120 },
}));

const Title = styled(Typography)({
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.25,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

// ---------- Composant ----------
const ResearchItem = ({
  img,
  name,
  eventID,
  debut,
  fin,
  lieu,
  type,
  organisateur,
  city,
  ev, // snapshot ou plain object
}) => {
  const isMobile = useMediaQuery("(max-width:900px)");

  // Support snapshot Firestore ou objet
  const data = useMemo(() => (ev && typeof ev.data === "function" ? ev.data() : ev.data) ?? {}, [ev]);

  const locationType = data?.locationType; // 1 => online (selon ton modèle)
  const seanceActive = data?.seanceActive === 1 || data?.seanceActive === true;
  const isMultiDates =
    seanceActive ||
    (debut && fin && toDateSafe(debut)?.toDateString() !== toDateSafe(fin)?.toDateString());

  const priceLabel = useMemo(() => formatPrice(data?.minPrice, "CFA"), [data?.minPrice]);
  const dateLabel = useMemo(() => formatRange(handleFirebaseTimestamp(debut), handleFirebaseTimestamp(fin)), [debut, fin]);


  const onImgError = (ev) => {
    ev.currentTarget.src =
      "https://cdn.jsdelivr.net/gh/placeholderlogo/img@main/placeholder-600x400.png";
  };

  return (
    <EventBox>
      <CardActionArea
        component={RouterLink}
        to={`/fr/event/${eventID}`}
        sx={{ p: { xs: 1, md: 2 } }}
        aria-label={`Ouvrir l’événement ${name}`}
      >
        <Stack direction="row" spacing={2} alignItems="stretch" justifyContent="space-between">
          {/* Colonne texte */}
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent={'space-between'} alignItems={'start'}>
              <Stack>
                <Title variant="body2" noWrap={false}>{name}</Title>
                {organisateur && (
                  <Typography fontSize={13} fontWeight={550} color="#999" sx={{ mt: 0.5 }}>
                    {organisateur}
                  </Typography>
                )}
              </Stack>
              {type && (
                <Chip
                  label={type}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: "#eee", color: "text.secondary" }}
                />
              )}
            </Stack>


            <Stack direction="column" spacing={1} sx={{ mt: 0.5 }}>
              {!!dateLabel && (
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                  <Typography color="#5E17EB" fontSize={16} fontWeight={500}>
                    {dateLabel}
                  </Typography>
                  {isMultiDates && (
                    <Chip
                      size="small"
                      label="Plusieurs dates"
                      sx={{ bgcolor: "#f6f3ff", color: "#5E17EB", fontWeight: 500 }}
                    />
                  )}
                </Stack>
              )}

              <Typography fontSize={15} color="text.secondary">
                {locationType === 1 ? "Événement en ligne" : [city, lieu].filter(Boolean).join(" - ")}
                {"  |  "}
                {priceLabel}
              </Typography>
            </Stack>
          </Box>

          {/* Colonne image + actions */}
          <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Img
              src={img}
              alt={name}
              onError={onImgError}
              loading="lazy"
              decoding="async"
              srcSet={`${img} 1x, ${img} 2x`}
              sizes="(max-width: 900px) 120px, 220px"
            />
          </Box>
        </Stack>
      </CardActionArea>
    </EventBox>
  );
};

export default memo(ResearchItem);
